import type { Organization, OrganizationOperationalSubscriptionStatus } from '@/types';

export interface OrganizationBillingState {
  effectiveStatus: OrganizationOperationalSubscriptionStatus;
  hasAccess: boolean;
  trialActive: boolean;
  paidCoverageActive: boolean;
  legacyGraceActive: boolean;
  trialEndsAt: Date | null;
  currentPeriodEnd: Date | null;
  legacyBillingGraceUntil: Date | null;
  trialDaysLeft: number;
}

function parseDate(value?: string | null) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getOrganizationBillingState(organization?: Organization | null): OrganizationBillingState {
  const now = Date.now();
  const trialEndsAt = parseDate(organization?.trial_ends_at);
  const currentPeriodEnd = parseDate(organization?.current_period_end);
  const legacyBillingGraceUntil = parseDate(organization?.legacy_billing_grace_until);
  const trialActive = Boolean(trialEndsAt && trialEndsAt.getTime() > now);
  const paidCoverageActive = Boolean(currentPeriodEnd && currentPeriodEnd.getTime() > now);
  const legacyGraceActive =
    organization?.subscription_status === 'active'
    && !trialEndsAt
    && !currentPeriodEnd
    && Boolean(legacyBillingGraceUntil && legacyBillingGraceUntil.getTime() > now);

  let effectiveStatus: OrganizationOperationalSubscriptionStatus = 'blocked';

  if (paidCoverageActive || legacyGraceActive) {
    effectiveStatus = organization?.subscription_status === 'past_due' ? 'past_due' : 'active';
  } else if (trialActive) {
    effectiveStatus = 'trialing';
  }

  return {
    effectiveStatus,
    hasAccess: trialActive || paidCoverageActive || legacyGraceActive,
    trialActive,
    paidCoverageActive,
    legacyGraceActive,
    trialEndsAt,
    currentPeriodEnd,
    legacyBillingGraceUntil,
    trialDaysLeft: trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now) / 86400000)) : 0,
  };
}
